
import { AudioLines, CircleAlert, Download, FileSpreadsheet, TriangleAlert } from 'lucide-react';

import { Button, Card, CardHead, EmptyState, Pill } from '../../components/ui';
import { placementApi } from '../../api/endpoints';
import { GapsPanel } from './GapsPanel';
import { InsertDetails } from './InsertDetails';
import { ScriptFeed } from './ScriptFeed';
import type { buildFeed } from './feed';
import { insertWord, mmss, placeWord, profileLabel, seconds } from './format';
import type { PlacementMedia, PlacementProposalView } from './types';
import { SHOW_EDIT_LIST } from './flags';
import styles from './placement.module.css';

export interface ProposalResultProps {
  proposal: PlacementProposalView;
  feed: ReturnType<typeof buildFeed>;
  mediaOf: (order: number) => PlacementMedia | null;
  overriddenOrders: ReadonlySet<number>;
  selectedOrder: number | null;
  onSelect: (order: number) => void;
  selectedItem: PlacementProposalView['items'][number] | null;
  onChoose: (media: PlacementMedia) => void;
  onResetChoice: () => void;
  onRerun: () => void;
  rerunLabel?: string;
  onAttachTimings?: () => void;
}

export function ProposalResult({
  proposal,
  feed,
  mediaOf,
  overriddenOrders,
  selectedOrder,
  onSelect,
  selectedItem,
  onChoose,
  onResetChoice,
  onRerun,
  rerunLabel = 'Подобрать заново',
  onAttachTimings,
}: ProposalResultProps) {
  const exact = proposal.timing === 'exact';
  const gaps = proposal.unmatched.length;
  const placed = proposal.items.length;
  const weak = proposal.items.filter((item) => item.weak).length;
  const withoutMedia = proposal.items.filter((item) => !mediaOf(item.order)?.url).length;

  return (
    <>
      <div className={styles.sourceBar}>
        <Pill tone={exact ? 'ok' : 'warn'} className={styles.srcChip}>
          <AudioLines size={13} strokeWidth={1.5} />
          {exact ? 'Тайм-коды точные' : 'Тайм-коды приблизительные'}
        </Pill>
        <span className={styles.srcActions}>
          <span title="Пресет подбора">
            <Pill>{profileLabel(proposal.density.profile)}</Pill>
          </span>
          <Pill>
            {proposal.totalWords.toLocaleString('ru-RU')} слов · {mmss(proposal.durationMs)}
          </Pill>
          <Pill>
            вставка от {seconds(proposal.density.minInsertMs)} до{' '}
            {seconds(proposal.density.maxInsertMs)}
          </Pill>
        </span>
      </div>

      {!exact && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <CircleAlert size={15} className={styles.bannerIconWarn} />
          <span className={styles.bannerText}>
            Приложите озвучку, и тайм-коды станут точными.
          </span>
          {onAttachTimings && (
            <Button size="sm" className={styles.bannerAction} onClick={onAttachTimings}>
              Приложить озвучку
            </Button>
          )}
        </div>
      )}

      {gaps > 0 && (
        <div className={`${styles.banner} ${styles.bannerDanger}`}>
          <TriangleAlert size={15} className={styles.bannerIconDanger} />
          <span className={styles.bannerText}>
            Для {gaps} {placeWord(gaps)} в библиотеке нет материала. Остальные {placed}{' '}
            расставлены.
          </span>
        </div>
      )}

      {weak > 0 && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <CircleAlert size={15} className={styles.bannerIconWarn} />
          <span className={styles.bannerText}>
            Под вопросом: {weak} {insertWord(weak)}. Нейросеть не нашла подходящего материала и
            поставила лучший из найденных; такие вставки помечены «сомнительно» — проверьте или
            замените на запасной вариант.
          </span>
        </div>
      )}

      {proposal.warnings.length > 0 && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <CircleAlert size={15} className={styles.bannerIconWarn} />
          <span className={styles.bannerText}>
            {proposal.warnings.map((warning) => (
              <div key={warning}>{warning}</div>
            ))}
          </span>
        </div>
      )}

      <div className={styles.resultWrap}>
        <Card>
          <CardHead
            title="Лента сценария"
            actions={
              <span className={styles.panelCount}>
                {placed} {insertWord(placed)} · {proposal.segments.length} абз.
              </span>
            }
          />

          {placed === 0 && gaps === 0 && proposal.skipped.length === 0 ? (
            <EmptyState
              icon={<CircleAlert size={18} strokeWidth={1.4} />}
              title="В тексте не нашлось мест под вставки"
              description="Текст короткий или слишком общий, показывать в кадре нечего."
            />
          ) : placed === 0 ? (
            <EmptyState
              icon={<CircleAlert size={18} strokeWidth={1.4} className={styles.iconDanger} />}
              title="Ни одной вставки не расставлено"
              description={`${gaps} без материала, ${proposal.skipped.length} убрано правилами плотности, ${proposal.rejected.length} отклонено. Подробности ниже.`}
            />
          ) : (
            <ScriptFeed
              rows={feed}
              timing={proposal.timing}
              mediaOf={mediaOf}
              overriddenOrders={overriddenOrders}
              selectedOrder={selectedOrder}
              onSelect={onSelect}
            />
          )}

          <div className={styles.panelFoot}>
            <span className={styles.footHint}>
              {overriddenOrders.size > 0 ? (
                <>
                  {overriddenOrders.size} {insertWord(overriddenOrders.size)} заменены вручную.
                  В архив и монтажный лист уйдёт исходный подбор, выбранные файлы скачивайте
                  в правой панели.
                </>
              ) : withoutMedia > 0 ? (
                <>
                  У {withoutMedia} {insertWord(withoutMedia)} нет ссылки на файл. Места в
                  монтажном листе верные.
                </>
              ) : gaps > 0 ? (
                <>
                  {placed} {insertWord(placed)} расставлены, {gaps} {placeWord(gaps)} останутся
                  пустыми.
                </>
              ) : null}
            </span>
            <Button size="sm" onClick={onRerun}>
              {rerunLabel}
            </Button>
            {SHOW_EDIT_LIST && (
              <a className={styles.linkBtn} href={placementApi.editListUrl(proposal.proposalId)} download>
                <FileSpreadsheet size={13} strokeWidth={1.6} /> Монтажный лист (CSV)
              </a>
            )}
            <a
              className={`${styles.linkBtn} ${styles.linkBtnPrimary}`}
              href={placementApi.exportUrl(proposal.proposalId)}
              download
            >
              <Download size={13} strokeWidth={1.6} /> Скачать всё (ZIP)
            </a>
          </div>
        </Card>

        <InsertDetails
          item={selectedItem}
          chosen={selectedItem ? mediaOf(selectedItem.order) : null}
          overridden={selectedItem ? overriddenOrders.has(selectedItem.order) : false}
          onChoose={onChoose}
          onReset={onResetChoice}
        />
      </div>

      <GapsPanel proposal={proposal} />
    </>
  );
}
